import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';

interface LoadingStateProps {
  /** Full-viewport centered spinner (auth/guards). */
  fullPage?: boolean;
  label?: string;
  className?: string;
  /** Skeleton placeholder rows instead of a spinner. */
  skeleton?: boolean;
  /** A small spinner and the label on one line, left-aligned: inside a dialog, popover or card row. */
  inline?: boolean;
  skeletonRows?: number;
}

/**
 * Loading indicator for pages and sections.
 */
export function LoadingState({
  fullPage = false,
  label = 'Loading…',
  className,
  skeleton = false,
  skeletonRows = 3,
  inline = false,
}: LoadingStateProps) {
  if (inline) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)}
      >
        <Spinner size="sm" />
        <span>{label}</span>
      </div>
    );
  }

  if (skeleton) {
    return (
      <div className={cn('space-y-3', className)} role="status" aria-label={label}>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-muted-foreground',
        fullPage && 'min-h-[100dvh] w-full',
        !fullPage && 'py-12',
        className
      )}
    >
      <Spinner size="xl" />
      <p className="text-sm font-medium">{label}</p>
    </div>
  );
}
